import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { z } from 'zod/v4';

import { useProfile } from '../../init/use-enforce-profile';
import { addResourceNameToQueryResult, useAxiosInstance } from './utils';
import type { ApiError } from './utils/types';

const NotificationsCountSchema = z
  .object({ notificationsCount: z.number() })
  .transform(data => data.notificationsCount);

export type NotificationsCount = z.infer<typeof NotificationsCountSchema>;

export async function loadNotificationsCountFn(axiosInstance: Promise<AxiosInstance>) {
  const instance = await axiosInstance;

  const { data } = await instance.get('/notifications/count');
  return NotificationsCountSchema.parse(data);
}

export function useLoadNotificationsCount() {
  const axiosInstance = useAxiosInstance();
  const profile = useProfile();

  const queryResult = useQuery<NotificationsCount, ApiError>({
    queryKey: ['notifications', 'count'],
    queryFn: () => loadNotificationsCountFn(axiosInstance),
    enabled: profile.hasAcceptedGc,
    placeholderData: 0,
  });

  return addResourceNameToQueryResult<NotificationsCount, ApiError, 'notificationsCount'>(
    'notificationsCount',
    queryResult,
  );
}
